import { Box, Typography, Link as MuiLink, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  colorsFoundation,
  colorsSemantic,
  borderRadius,
} from "@accredo/fe-shared/theme/index.js";
import { getClaimFormIcon } from "../utils/claimFormIcons.js";
import typography from "@accredo/fe-shared/theme/tokens/typography.js";

BranchSelector.propTypes = {
  data: PropTypes.array,
  selectedSparte: PropTypes.number,
  setSelectedSparte: PropTypes.func,
  setExpanded: PropTypes.func,
};

const getIcon = (icon_key, disabled) => {
  const Icon = getClaimFormIcon(icon_key);
  return (
    <Icon
      sx={{
        fontSize: 32,
        color: disabled
          ? colorsSemantic.text.disabled
          : colorsFoundation.neutral[700],
      }}
    />
  );
};

// The Objektsparten with a coverage type are one card per claim form, labelled
// with the form; selecting it loads the form through its first member Sparte.
// Sparten are identified by their catalog id (`sparte_id`), `sparte` is the
// catalog name shown to the user.
function isCoverageCard(item) {
  return Boolean(item.coverage_type && item.claim_form_id);
}

function toCards(data) {
  const cards = [];
  const seen_forms = new Set();
  data.forEach((item) => {
    if (!isCoverageCard(item)) {
      cards.push(item);
    } else if (!seen_forms.has(item.claim_form_id)) {
      seen_forms.add(item.claim_form_id);
      cards.push({
        ...item,
        title: item.claim_form_label,
        beschreibung: item.claim_form_description,
        members: data
          .filter((other) => other.claim_form_id === item.claim_form_id)
          .map((other) => other.sparte_id),
      });
    }
  });
  return cards;
}

// The name an Objektsparte goes by once chosen: the claim form for the
// coverage-typed ones (they are one card per form), otherwise the Sparte.
export function getBranchName(objekt_sparte) {
  return objekt_sparte && isCoverageCard(objekt_sparte)
    ? objekt_sparte.claim_form_label
    : objekt_sparte?.sparte;
}

export default function BranchSelector(props) {
  const { data, selectedSparte, setSelectedSparte, setExpanded } = props;
  const { enqueueSnackbar } = useSnackbar();

  const handleSelect = (item) => {
    if (item.has_claim_form === false) {
      enqueueSnackbar(
        "Für diese Sparte können im Moment noch keine Schäden über das Portal eingereicht werden, bitte wenden Sie sich an Ihren Ansprechpartner.",
        { variant: "info" }
      );
      return;
    }
    setSelectedSparte(item.sparte_id);
    if (setExpanded) {
      setExpanded(1);
    }
  };

  const BranchCard = ({ item }) => {
    const isSelected = (item.members ?? [item.sparte_id]).includes(
      selectedSparte
    );
    const isDisabled = item.has_claim_form === false;
    const textColor = isDisabled
      ? colorsSemantic.text.disabled
      : colorsFoundation.neutral.softblack;

    return (
      <Box
        onClick={() => handleSelect(item)}
        aria-disabled={isDisabled}
        sx={{
          width: "100%", // Anpassung an die Grid-Spalte
          backgroundColor: colorsFoundation.neutral[50],
          borderRadius: borderRadius.xs,
          px: 3,
          py: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
          border: isSelected
            ? `2px solid ${colorsFoundation.key.gold[600]}`
            : "2px solid transparent",
          transition: "all 0.2s ease-in-out",
          "&:hover": isDisabled
            ? {}
            : {
                border: `2px solid ${colorsFoundation.key.gold[600]}`,
                backgroundColor: `${colorsFoundation.key.gold[50]}`,
              },
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getIcon(item.icon_key, isDisabled)}
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontWeight: typography.weight.semibold,
            fontSize: typography.size.lg,
            lineHeight: 1.5,
            color: textColor,
            textAlign: "center",
          }}
        >
          {item.title ?? item.sparte}
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.5,
            color: textColor,
            textAlign: "center",
          }}
        >
          {item.beschreibung}
        </Typography>

        {/* Melden Link */}
        <MuiLink
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(item);
          }}
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.5,
            color: textColor,
            textDecoration: "none",
            cursor: isDisabled ? "not-allowed" : "pointer",
            px: 1.5,
            py: 1,
            "&:hover": {
              textDecoration: isDisabled ? "none" : "underline",
            },
          }}
        >
          Melden
        </MuiLink>
      </Box>
    );
  };

  return (
    <Box>
      <Typography
        sx={{
          mb: 2,
          fontSize: "1rem",
          color: colorsFoundation.neutral.softblack,
        }}
      >
        Um welche Art Schaden handelt es sich? Wählen Sie die Versicherungsart
        aus.
      </Typography>

      <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
        {toCards(data).map((item, index) => (
          <Grid
            key={item.sparte_id + "-" + index}
            sx={{ display: "flex" }}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <BranchCard item={item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
